import { useEffect } from 'react'
import { useFlowStore } from '../store'
import { AgentState } from '../types'
import { detectUnsavedChanges } from '../utils'

interface UseNavigationBlockingProps {
  flowData?: {
    phlo?: {
      state?: AgentState
    }
  }
}

export const useNavigationBlocking = ({ flowData }: UseNavigationBlockingProps) => {
  // Handle all navigation blocking (page refresh, browser navigation, React Router navigation)
  useEffect(() => {
    const getCurrentState = () => ({
      isEditing: useFlowStore.getState().isEditing || flowData?.phlo?.state === AgentState.DRAFT,
      hasUnsavedChanges: detectUnsavedChanges(),
    })

    const shouldBlock = () => {
      const { isEditing: currentIsEditing, hasUnsavedChanges } = getCurrentState()
      return currentIsEditing && hasUnsavedChanges
    }

    // 1. Handle page refresh, tab closing, external navigation
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (shouldBlock()) {
        event.preventDefault()
        return 'You have unsaved changes. Are you sure you want to leave?'
      }
      return undefined
    }

    // ITs getting unmounted when the component is unmounted, we will move to this outer (location-effects-handler) useffect later and make it more robust
    // 2. Handle browser back/forward button navigation (this is the key)
    // window.history.pushState(null, '', window.location.href)

    // const handlePopState = () => {
    //   if (shouldBlock()) {
    //     const confirmed = window.confirm(
    //       'You have unsaved changes. Are you sure you want to leave?',
    //     )
    //     if (!confirmed) {
    //       // window.history.pushState(null, '', window.location.href)
    //       return
    //     }
    //     // If user confirms, remove the event listener temporarily to avoid loop
    //     // window.removeEventListener('popstate', handlePopState)
    //     window.history.back()
    //   }
    //   // If no unsaved changes, allow navigation normally
    //   // window.history.go(-2)
    // }

    // 3. Handle React Router navigation (Link clicks)
    const handleLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const link = target.closest('a[href]') as HTMLAnchorElement

      // Only intercept internal navigation links (React Router links)
      if (
        link?.href &&
        !link.target &&
        link.hostname === window.location.hostname &&
        !link.hasAttribute('data-external')
      ) {
        if (shouldBlock()) {
          const confirmed = window.confirm(
            'You have unsaved changes. Are you sure you want to leave?',
          )
          if (!confirmed) {
            event.preventDefault()
            event.stopPropagation()
            return false
          }
        }
      }
      return undefined
    }

    // Add all event listeners
    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('click', handleLinkClick, true)

    // Cleanup function for all listeners
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('click', handleLinkClick, true)
    }
  }, [flowData?.phlo?.state])
  const isEditing = useFlowStore.getState().isEditing || flowData?.phlo?.state === AgentState.DRAFT

  return { isEditing }
}
